/**
 * useParamsWriter.ts
 */
import Focus from '@shared/types/enums/focus';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import MainProps from '@shared/types/props/main';
import { MainTimeline } from '@shared/types/interfaces/main';
import { MutableRefObject, useRef } from 'react';
import { store } from '@shared/store/store';
import { updateParams as updateParamsSlice } from '@shared/store/slices/router.slice';
import { useAppDispatch } from '@shared/store/hooks';

interface ParamsWriterProps {
  channelsRef: MutableRefObject<Rail | undefined>;
  filterRef: MutableRefObject<Rail | undefined>;
  flagsRef: MutableRefObject<MainProps['flags']>;
  railsRef: MutableRefObject<Rail[] | undefined>;
  slidersRef: MutableRefObject<{ id: string; data: Slider[] | undefined }>;
  timelineRef: MutableRefObject<MainTimeline | null>;
}

/**
 * Writes/schedules a snapshot of the Main page's state into
 * `router.params.current`, for `hydrateMain` (in useMain.ts) to restore on
 * back-navigation.
 */
export default function useParamsWriter({
  channelsRef,
  filterRef,
  flagsRef,
  railsRef,
  slidersRef,
  timelineRef,
}: ParamsWriterProps) {
  const dispatch = useAppDispatch();
  const paramsWriteTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  /**
   * Writes a snapshot into `router.params.current`. Always synchronous;
   * cancels any pending deferred write first so a stale write can't land
   * after.
   */
  function writeParamsNow() {
    cancelScheduledParamsWrite();
    const { focus: focusSlice, main: mainSlice } = store.getState();
    dispatch(
      updateParamsSlice({
        // Content
        channels: channelsRef.current,
        filter: filterRef.current,
        flags: flagsRef.current,
        rails: railsRef.current,
        sliders: slidersRef.current.data,
        timeline: timelineRef.current,
        // Hook params
        asset: mainSlice.asset,
        category: mainSlice.category,
        complete: flagsRef.current?.complete || false,
        focus:
          focusSlice.focus === Focus.FLOATING_MENU
            ? focusSlice.stack[focusSlice.stack.length - 1] ?? focusSlice.focus
            : focusSlice.focus,
        layout: mainSlice.layout,
        selection: mainSlice.selection,
      })
    );
  }

  /**
   * Cancels a pending deferred write, if any, without flushing it.
   */
  function cancelScheduledParamsWrite() {
    if (paramsWriteTimeoutRef.current !== undefined) {
      clearTimeout(paramsWriteTimeoutRef.current);
      paramsWriteTimeoutRef.current = undefined;
    }
  }

  /**
   * Defers the write to a macrotask so the router dispatch doesn't block
   * paint of the caller's synchronous updates.
   */
  function scheduleParamsWrite() {
    cancelScheduledParamsWrite();
    paramsWriteTimeoutRef.current = setTimeout(() => {
      paramsWriteTimeoutRef.current = undefined;
      writeParamsNow();
    }, 0);
  }

  return { writeParamsNow, cancelScheduledParamsWrite, scheduleParamsWrite };
}
